import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Loader2, Trash2, CalendarClock } from "lucide-react";
import { api, inr } from "../../lib/api";
import { Field, Modal } from "../admin/AdminRestaurants";
import { toast } from "sonner";
import MultiImageUploader from "../../components/MultiImageUploader";
import ProductVariantsEditor from "../../components/ProductVariantsEditor";
import ProductVariantsMatrixEditor from "../../components/ProductVariantsMatrixEditor";
import { isFoodShop } from "../../lib/foodCategory";
import { getCatalogLabel } from "../../lib/catalogLabel";
import { useLse } from "../../lib/lse";

const EMPTY = {
    name: "", description: "", food_type: "VEG", category_ids: [], images: [],
    menu_price: "", // base rate; admin layers SP & MRP on top
    unit_of_measure: "",
    // optional per-product overrides (null = inherit from restaurant)
    sp_increment_type: "", sp_increment_value: "",
    mrp_increment_type: "", mrp_increment_value: "",
    commission_type: "", commission_value: "",
    gst_percent: "",
    variants: [],
    // iter119 — Combinations mode (Color × Size matrix editor) vs
    // flat single-axis mode. Auto-detected on edit from ``option_values``.
    variant_matrix_mode: false,
    // Service-only fields
    kind: "PRODUCT",
    duration_min: 60,
    slot_minutes: 30,
    advance_days: 7,
    weekday_open: "09:00",
    weekday_close: "20:00",
    advance_payment_pct: 20,
    location_type: "AT_CUSTOMER",
};

export default function MerchantMenu() {
    // Location Service Engine: brand + SKU fields (only when the tenant has product_brand_sku on).
    const brandSku = useLse().has("product_brand_sku");
    const [rests, setRests] = useState([]);
    const [rid, setRid] = useState("");
    const [rows, setRows] = useState([]);
    const [cats, setCats] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/merchant/restaurants").then(({ data }) => {
            setRests(data); if (data?.[0]) setRid(data[0].id);
        });
        // Pull verticals so we can resolve UUID-stored business_category_ids
        // back to their slug — drives food/non-food gating.
        api.get("/business-categories")
            .then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, []);
    const load = () => {
        if (!rid) return;
        api.get(`/merchant/products?restaurant_id=${rid}`).then(({ data }) => setRows(data));
        api.get(`/merchant/categories?restaurant_id=${rid}`).then(({ data }) => setCats(data));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

    const selectedShop = useMemo(() => rests.find((r) => r.id === rid), [rests, rid]);
    const showFoodType = isFoodShop(selectedShop, verticals);
    const catalogLabel = getCatalogLabel(selectedShop, verticals);
    const parentCats = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
    const subsBy = useMemo(() => {
        const m = {};
        for (const c of cats) if (c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
        return m;
    }, [cats]);

    const openCreate = (kind = "PRODUCT") => { setEditId(null); setForm({ ...EMPTY, kind }); setOpen(true); };
    const openEdit = (p) => {
        setEditId(p.id);
        const cfg = p.service_config || {};
        setForm({
            name: p.name || "", description: p.description || "",
            food_type: p.food_type || "VEG",
            category_ids: p.category_ids || (p.category_id ? [p.category_id] : []),
            // iter121 — full image list (fallback to legacy single-image
            // for products that pre-date multi-image support).
            images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
            menu_price: p.menu_price ?? p.selling_price ?? "",
            unit_of_measure: p.unit_of_measure || "",
            sp_increment_type: p.sp_increment_type || "",
            sp_increment_value: p.sp_increment_value ?? "",
            mrp_increment_type: p.mrp_increment_type || "",
            mrp_increment_value: p.mrp_increment_value ?? "",
            commission_type: p.commission_type || "",
            commission_value: p.commission_value ?? "",
            gst_percent: p.gst_percent ?? "",
            brand: p.brand || "", sku: p.sku || "",
            variants: Array.isArray(p.variants) ? p.variants : [],
            // iter119 — matrix mode is auto-detected: any variant with
            // ``option_values`` containing 2+ axes flips the toggle ON.
            variant_matrix_mode: (p.variants || []).some((v) => {
                const ov = v?.option_values || {};
                return Object.keys(ov).length >= 2;
            }),
            kind: p.kind || "PRODUCT",
            duration_min: cfg.duration_min ?? 60,
            slot_minutes: cfg.slot_minutes ?? 30,
            advance_days: cfg.advance_days ?? 7,
            weekday_open: cfg.weekday_open ?? "09:00",
            weekday_close: cfg.weekday_close ?? "20:00",
            advance_payment_pct: cfg.advance_payment_pct ?? 20,
            location_type: cfg.location_type ?? "AT_CUSTOMER",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name || !form.menu_price) { toast.error("Name & Menu price required"); return; }
        setBusy(true);
        const isService = form.kind === "SERVICE";
        const payload = {
            name: form.name, description: form.description,
            // Veg/Non-Veg only meaningful for food shops; persist neutral default otherwise.
            food_type: showFoodType ? form.food_type : "VEG",
            category_ids: form.category_ids,
            category_name: cats.find((c) => c.id === form.category_ids[0])?.name || null,
            category_id: form.category_ids[0] || null,
            images: Array.isArray(form.images) ? form.images.filter(Boolean) : [],
            menu_price: Number(form.menu_price),
            unit_of_measure: (form.unit_of_measure || "").trim() || null,
            sp_increment_type: form.sp_increment_type || null,
            sp_increment_value: form.sp_increment_value === "" ? null : Number(form.sp_increment_value),
            mrp_increment_type: form.mrp_increment_type || null,
            mrp_increment_value: form.mrp_increment_value === "" ? null : Number(form.mrp_increment_value),
            commission_type: form.commission_type || null,
            commission_value: form.commission_value === "" ? null : Number(form.commission_value),
            gst_percent: form.gst_percent === "" ? null : Number(form.gst_percent),
            variants: (form.variants || []).filter((v) => (v.label || "").trim()).map((v, idx) => ({
                ...v,
                sort_order: idx,
                selling_price: Number(v.selling_price) || 0,
                mrp: Number(v.mrp) || 0,
            })),
            kind: form.kind,
            service_config: isService ? {
                duration_min: Number(form.duration_min) || 60,
                slot_minutes: Number(form.slot_minutes) || 30,
                advance_days: Number(form.advance_days) || 7,
                weekday_open: form.weekday_open || "09:00",
                weekday_close: form.weekday_close || "20:00",
                advance_payment_pct: Number(form.advance_payment_pct) || 0,
                location_type: form.location_type === "AT_SHOP" ? "AT_SHOP" : "AT_CUSTOMER",
            } : null,
            ...(brandSku ? { brand: (form.brand || "").trim() || null, sku: (form.sku || "").trim() || null } : {}),
        };
        try {
            if (editId) await api.patch(`/merchant/products/${editId}`, payload);
            else await api.post(`/merchant/products`, { restaurant_id: rid, ...payload });
            toast.success(editId ? "Updated" : (isService ? "Service added" : "Added")); setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this product?")) return;
        await api.delete(`/merchant/products/${id}`); toast.success("Removed"); load();
    };

    const toggleCat = (cid) => setForm((f) => ({
        ...f,
        category_ids: f.category_ids.includes(cid) ? f.category_ids.filter((x) => x !== cid) : [...f.category_ids, cid],
    }));

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold" data-testid="merchant-menu-heading">{catalogLabel}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} items • You set the <strong>{catalogLabel} Price</strong>; admin adds Selling/MRP markups for customers.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select data-testid="menu-rest-picker" value={rid} onChange={(e) => setRid(e.target.value)} className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold">
                        {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button data-testid="add-menu-item" onClick={() => openCreate("PRODUCT")} disabled={!rid} className="bg-primary text-primary-foreground rounded-xl px-3 py-2 font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Plus size={16} /> Add item
                    </button>
                    <button data-testid="add-menu-service" onClick={() => openCreate("SERVICE")} disabled={!rid} className="bg-violet-600 text-white rounded-xl px-3 py-2 font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                        <CalendarClock size={16} /> Add service
                    </button>
                </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((p) => (
                    <div key={p.id} className="bg-card border border-border rounded-2xl p-4 flex gap-3" data-testid={`menu-item-${p.id}`}>
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                            {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <p className="font-bold text-sm line-clamp-1">{p.name}</p>
                                {p.kind === "SERVICE" && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" data-testid={`menu-svc-badge-${p.id}`}><CalendarClock size={9} /> Service</span>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {(p.category_ids || []).map((cid) => cats.find((c) => c.id === cid)?.name).filter(Boolean).join(", ") || p.category_name || "—"}{showFoodType ? ` • ${p.food_type}` : ""}
                                {p.kind === "SERVICE" && p.service_config?.duration_min && <> • {p.service_config.duration_min} min</>}
                            </p>
                            <p className="text-xs mt-1">Menu <strong>{inr(p.menu_price ?? p.selling_price)}</strong> • Sell <strong>{inr(p.selling_price)}</strong></p>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${p.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{p.status}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                            <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {open && (
                <Modal title={editId ? `Edit ${form.kind === "SERVICE" ? "service" : "item"}` : (form.kind === "SERVICE" ? "Add service" : "Add menu item")} onClose={() => setOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Name"><input data-testid="m-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        {brandSku && (
                            <>
                                <Field label="Brand"><input data-testid="m-brand" className="inp" maxLength={80} value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
                                <Field label="SKU"><input data-testid="m-sku" className="inp" maxLength={64} value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Field>
                            </>
                        )}
                        {form.kind !== "SERVICE" && showFoodType && (
                            <Field label="Type"><select className="inp" value={form.food_type} onChange={(e) => setForm({ ...form, food_type: e.target.value })}><option>VEG</option><option>NON_VEG</option><option>EGG</option></select></Field>
                        )}
                        <Field label={form.kind === "SERVICE" ? "Service Price (₹)" : "Menu Price (₹)"}><input data-testid="m-menu-price" type="number" className="inp" value={form.menu_price} onChange={(e) => setForm({ ...form, menu_price: e.target.value })} /></Field>
                        <Field label="GST % (optional)">
                            <input
                                data-testid="m-gst-percent"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="inp"
                                value={form.gst_percent}
                                onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                                placeholder="Inherits from category / shop"
                            />
                            <span className="text-[11px] text-muted-foreground mt-1 block">
                                Leave blank to inherit. <code>0</code> = tax-free.
                            </span>
                        </Field>
                        <Field full label="Categories (pick one or more)">
                            <div className="space-y-2">
                                {parentCats.map((p) => {
                                    const subs = subsBy[p.id] || [];
                                    const onP = form.category_ids.includes(p.id);
                                    return (
                                        <div key={p.id}>
                                            <button type="button" onClick={() => toggleCat(p.id, null)} data-testid={`cat-pick-${p.id}`}
                                                className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${onP ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
                                                {p.name}
                                            </button>
                                            {subs.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 mt-1.5 ml-4">
                                                    {subs.map((s) => {
                                                        const on = form.category_ids.includes(s.id);
                                                        return (
                                                            <button type="button" key={s.id} onClick={() => toggleCat(s.id, p.id)} data-testid={`sub-pick-${s.id}`}
                                                                className={`text-[11px] rounded-full px-2.5 py-1 border ${on ? "bg-primary/15 text-primary border-primary/40" : "bg-muted border-border text-muted-foreground"}`}>
                                                                {s.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {parentCats.length === 0 && <span className="text-xs text-muted-foreground">No categories yet — create one in &quot;Categories&quot;.</span>}
                            </div>
                        </Field>
                        <Field full label="Product images">
                            <MultiImageUploader
                                value={form.images}
                                onChange={(imgs) => setForm({ ...form, images: imgs })}
                                testId="mmenu-images"
                            />
                        </Field>
                        <Field full label="Description"><textarea rows={2} className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                        <Field full label="Unit of measure (optional)">
                            <input
                                data-testid="mmenu-uom"
                                className="inp"
                                value={form.unit_of_measure}
                                onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
                                placeholder="e.g. 500g, 1 ltr, 12 pcs, per kg"
                            />
                            <span className="text-[11px] text-muted-foreground mt-1 block">
                                Rendered next to the price on the storefront. Each variant can override this with its own UoM.
                            </span>
                        </Field>
                    </div>

                    {form.kind !== "SERVICE" && (
                        <div className="mt-4 border-t border-border pt-4">
                            {/* iter119 — toggle between flat variant list
                                (single-axis: Size only or Color only) and
                                matrix editor (Color × Size combinations).
                                Auto-detects matrix mode from ``option_values``
                                on any existing variant so re-editing a
                                matrix product opens the correct editor. */}
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Variants</p>
                                    <p className="text-[11px] text-muted-foreground">
                                        {form.variant_matrix_mode
                                            ? "Combinations mode — every axis pair auto-generates a SKU."
                                            : "Simple mode — each row is a standalone SKU (Size or Color only)."}
                                    </p>
                                </div>
                                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="mmenu-variant-matrix-toggle"
                                        checked={!!form.variant_matrix_mode}
                                        onChange={(e) => setForm({
                                            ...form,
                                            variant_matrix_mode: e.target.checked,
                                            // When switching modes, clear the previous
                                            // list so the merchant starts clean instead
                                            // of migrating malformed data.
                                            variants: [],
                                        })}
                                        className="accent-primary"
                                    />
                                    <span className="font-semibold">Combinations (Color × Size)</span>
                                </label>
                            </div>
                            {form.variant_matrix_mode ? (
                                <ProductVariantsMatrixEditor
                                    value={form.variants}
                                    onChange={(vs) => setForm({ ...form, variants: vs })}
                                    testId="mmenu-variants-matrix"
                                />
                            ) : (
                                <ProductVariantsEditor
                                    value={form.variants}
                                    onChange={(vs) => setForm({ ...form, variants: vs })}
                                    testId="mmenu-variants"
                                />
                            )}
                        </div>
                    )}

                    {form.kind === "SERVICE" && (
                        <div className="mt-4 border-t border-border pt-4">
                            <h3 className="text-sm font-bold inline-flex items-center gap-2 mb-3"><CalendarClock size={14} className="text-violet-700" /> Booking settings</h3>
                            <Field label="Where does this happen?">
                                <div className="grid grid-cols-2 gap-2" data-testid="svc-location-type">
                                    <button
                                        type="button"
                                        data-testid="svc-location-at-customer"
                                        onClick={() => setForm({ ...form, location_type: "AT_CUSTOMER" })}
                                        className={`rounded-lg border px-3 py-2 text-xs font-semibold text-left ${form.location_type !== "AT_SHOP" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-border text-muted-foreground"}`}
                                    >
                                        At customer&apos;s address
                                        <span className="block font-normal opacity-80">e.g. home cook, cleaning</span>
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="svc-location-at-shop"
                                        onClick={() => setForm({ ...form, location_type: "AT_SHOP" })}
                                        className={`rounded-lg border px-3 py-2 text-xs font-semibold text-left ${form.location_type === "AT_SHOP" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-border text-muted-foreground"}`}
                                    >
                                        At my shop
                                        <span className="block font-normal opacity-80">customer visits you</span>
                                    </button>
                                </div>
                            </Field>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <Field label="Duration (min)"><input data-testid="svc-duration" type="number" className="inp" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></Field>
                                <Field label="Slot every (min)"><input data-testid="svc-slot" type="number" className="inp" value={form.slot_minutes} onChange={(e) => setForm({ ...form, slot_minutes: e.target.value })} /></Field>
                                <Field label="Open at"><input data-testid="svc-open" type="time" className="inp" value={form.weekday_open} onChange={(e) => setForm({ ...form, weekday_open: e.target.value })} /></Field>
                                <Field label="Close at"><input data-testid="svc-close" type="time" className="inp" value={form.weekday_close} onChange={(e) => setForm({ ...form, weekday_close: e.target.value })} /></Field>
                                <Field label="Book up to (days ahead)"><input data-testid="svc-advance-days" type="number" className="inp" value={form.advance_days} onChange={(e) => setForm({ ...form, advance_days: e.target.value })} /></Field>
                                <Field label="Advance payment (%)"><input data-testid="svc-advance-pct" type="number" className="inp" value={form.advance_payment_pct} onChange={(e) => setForm({ ...form, advance_payment_pct: e.target.value })} /></Field>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-2">Customers will see {form.advance_payment_pct || 0}% advance ({form.menu_price ? `₹${Math.round((Number(form.menu_price) * (Number(form.advance_payment_pct) || 0)) / 100)}` : "—"}) charged at booking via mock Razorpay. Remainder due at service.</p>
                        </div>
                    )}

                    <button data-testid="m-save" disabled={busy || !form.name || !form.menu_price} onClick={submit}
                        className={`mt-4 w-full ${form.kind === "SERVICE" ? "bg-violet-600 text-white" : "bg-primary text-primary-foreground"} rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50`}>
                        {busy && <Loader2 size={16} className="animate-spin" />} Save {form.kind === "SERVICE" ? "service" : "item"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

function OverrideRow() {
    // No-op: per-product pricing overrides are admin-only; merchant view doesn't render this.
    return null;
}
